import * as React from 'react';
import Box from '../../Box/Box';
import Button from '../../Button/Button';
import HStack from '../../HStack/HStack';
import Icon from '../../Icon/Icon';
import NewCheckbox from '../../NewCheckbox/NewCheckbox';
import Search from '../../Search/Search';
import Tag from '../../Tag/Tag';
import Text from '../../Typography/Text/Text';
import VStack from '../../VStack/VStack';

import { FilterComponentProps } from '../types';

export interface SearchListProps<T extends any> extends FilterComponentProps<T[]> {
  /** Filter panel title */
  title?: string;
  /** Search input field placeholder */
  searchPlaceholder?: string;
  /** Returns a unique identifier for an option item */
  getItemKey?: (item: T) => string | number;
  /** Returns a string representation for an option item */
  getItemLabel?: (item: T) => string;
  /** Array of search options */
  options: T[];
}

export const SearchListFilter = <T extends any>({
  title,
  searchPlaceholder = 'Search',
  getItemKey = item => String(item),
  getItemLabel = item => String(item),
  options,
  ...props
}: SearchListProps<T>) => {
  if (props.setValue === undefined) {
    throw new Error('SearchListFilter: missing required props - did you forget to spread {...props}?');
  }
  if (!Array.isArray(props.value)) {
    throw new Error(`SearchListFilter: type of value must be an array, currently ${typeof props.value}`);
  }

  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState<Record<string, T>>(() =>
    props.value.reduce((acc, item) => {
      acc[getItemKey(item)] = item;
      return acc;
    }, {}),
  );

  const searchQuery = search.toLowerCase();

  return (
    <>
      <VStack spacing="16">
        <Search
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="full"
        />
        <Box>
          <HStack align="distributed">
            <HStack spacing="4" alignY="center">
              <Text variant="body-2">Selected</Text>
            </HStack>
            <Button
              type="transparent"
              size="small"
              onClick={() => {
                setSelected({});
                props.reset();
              }}
            >
              Deselect all
            </Button>
          </HStack>
          {props.value.length > 0 ? (
            <HStack spacing="4" wraps>
              {props.value.map(option => {
                const key = getItemKey(option);
                return (
                  <Tag
                    size="mini"
                    key={`${key}-tag`}
                    action={p => (
                      <Tag.Button
                        {...p}
                        onClick={() => {
                          const state = { ...selected };
                          delete state[key];
                          setSelected(state);
                          props.setValue(Object.values(state));
                        }}
                      >
                        <Icon type="close" size="sm" />
                      </Tag.Button>
                    )}
                  >
                    {getItemLabel(option)}&nbsp;
                  </Tag>
                );
              })}
            </HStack>
          ) : (
            <HStack align="center">
              <Text trim variant="body-3-feedback-neutral">
                No filters applied
              </Text>
            </HStack>
          )}
        </Box>
        <Box>
          {title && (
            <HStack spacing="4" alignY="center">
              <Text variant="body-2">{title}</Text>
            </HStack>
          )}

          {options
            .filter(option => getItemLabel(option).toLowerCase().includes(searchQuery))
            .map(option => {
              const id = getItemKey(option);

              return (
                <NewCheckbox
                  id={id as string}
                  key={id}
                  value={id}
                  label={getItemLabel(option)}
                  checked={!!selected[id]}
                  onChange={e => {
                    const state = { ...selected };
                    if (e.target.checked) {
                      state[id] = option;
                    } else {
                      delete state[id];
                    }
                    setSelected(state);
                    props.setValue(Object.values(state));
                  }}
                />
              );
            })}
        </Box>
      </VStack>
    </>
  );
};

SearchListFilter.displayName = 'GlobalFilter.SearchListFilter';
